import type { LucideIcon } from "lucide-react";
import type * as React from "react";

export interface DetailMetaItem {
  /** Stable key for React list rendering. */
  key: string;
  icon: LucideIcon;
  node: React.ReactNode;
}

interface EntityDetailHeaderProps {
  /** Leading visual — a PersonAvatar, or an icon/level tile. */
  visual: React.ReactNode;
  title: string;
  /**
   * Badge row rendered directly under the title, ahead of the subtitle — for
   * chips that read as part of the entity's identity (a role's career levels)
   * rather than as top-right status.
   */
  titleBadges?: React.ReactNode;
  /** Secondary line under the title (description, role title, etc.). */
  subtitle?: React.ReactNode;
  /** Wrapping meta line of icon + value pairs (email, category, holders, …). */
  meta?: DetailMetaItem[];
  /** Status / classification badges, top-right. */
  badges?: React.ReactNode;
  /** Right-aligned actions (Email, Edit, …). */
  actions?: React.ReactNode;
}

/**
 * Shared detail-page header: leading visual + title + subtitle + meta line on
 * the left, badges + actions stacked on the right. Used by every entity
 * drill-in page (person, role, skill, competency, certification).
 */
export function EntityDetailHeader({
  visual,
  title,
  titleBadges,
  subtitle,
  meta,
  badges,
  actions,
}: EntityDetailHeaderProps) {
  return (
    <div className="flex flex-col gap-5 rounded-xl border bg-card p-5 sm:flex-row sm:items-start">
      {visual}

      <div className="min-w-0 flex-1">
        <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">{title}</h1>
        {titleBadges && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">{titleBadges}</div>
        )}
        {subtitle && <div className="mt-0.5 text-sm text-muted-foreground">{subtitle}</div>}
        {meta && meta.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
            {meta.map((item) => {
              const Icon = item.icon;
              return (
                <span key={item.key} className="inline-flex items-center gap-1.5">
                  <Icon className="size-3.5 opacity-70" />
                  {item.node}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {(badges || actions) && (
        <div className="flex flex-col items-start gap-2.5 sm:items-end">
          {badges && <div className="flex flex-wrap items-center gap-1.5">{badges}</div>}
          {actions && <div className="flex flex-wrap items-center gap-1.5">{actions}</div>}
        </div>
      )}
    </div>
  );
}
